/**
 * useTimezone composable — @tms-phoenix/ui-vue
 *
 * Provides the browser's system timezone and convenience wrappers around the
 * datetime utility functions that are pre-bound to that timezone.
 *
 * All date/time values received from the API (UTC) are converted to the
 * browser's system timezone for display, and all values entered by the user
 * are treated as being in the system timezone before conversion to UTC for
 * the API.
 *
 * Usage:
 * ```vue
 * <script setup lang="ts">
 * import { useTimezone } from '@tms-phoenix/ui-vue';
 * const { timeZone, formatDateTime, formatDate, utcIsoToPickerDate, pickerDateToUtcIso } = useTimezone();
 * </script>
 * ```
 */
import {
  formatDateTime as _formatDateTime,
  formatDate as _formatDate,
  utcIsoToPickerDate as _utcIsoToPickerDate,
  pickerDateToUtcIso as _pickerDateToUtcIso,
  utcIsoToDatetimeLocal as _utcIsoToDatetimeLocal,
  nowPlusHoursInUserTz as _nowPlusHoursInUserTz,
  todayInUserTz as _todayInUserTz,
  isBeforeToday as _isBeforeToday,
  parseDateOnlyToPickerDate as _parseDateOnlyToPickerDate,
  pickerDateToDateOnly as _pickerDateToDateOnly,
  nowUtcIso,
} from '../utils/datetime.util';

export { nowUtcIso };

export function useTimezone() {
  /**
   * IANA timezone string — the browser's system timezone at the time this
   * composable is called. Captured once as a plain string; it will not
   * automatically update if the user changes their OS timezone mid-session.
   */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    /** IANA timezone string (browser system timezone, captured at call time). */
    timeZone,

    /**
     * Format a UTC ISO string as `dd/MM/yyyy HH:mm` in the user's timezone.
     * Returns `'—'` for null/undefined/invalid input.
     */
    formatDateTime: (utcIso: string | null | undefined): string =>
      _formatDateTime(utcIso, timeZone),

    /**
     * Format a UTC ISO string or bare `YYYY-MM-DD` date as `dd/MM/yyyy` in
     * the user's timezone.  Bare date strings are NOT timezone-shifted.
     * Returns `'—'` for null/undefined/invalid input.
     */
    formatDate: (value: string | null | undefined): string =>
      _formatDate(value, timeZone),

    /**
     * Convert a UTC ISO string to a `Date` for PrimeVue DatePicker.
     * The returned Date's browser-local representation matches the UTC
     * instant's wall-clock time in the user's timezone.
     */
    utcIsoToPickerDate: (utcIso: string | null | undefined): Date | null =>
      _utcIsoToPickerDate(utcIso, timeZone),

    /**
     * Convert a DatePicker-selected `Date` back to a UTC ISO string.
     * Interprets the picker value as user-timezone wall clock, not browser local.
     */
    pickerDateToUtcIso: (d: Date | null | undefined): string | undefined =>
      _pickerDateToUtcIso(d, timeZone),

    /**
     * Convert a UTC ISO string to `YYYY-MM-DDTHH:mm` in the user's timezone.
     * Use as the `value` of `<input type="datetime-local">`.
     */
    utcIsoToDatetimeLocal: (utcIso: string | null | undefined): string =>
      _utcIsoToDatetimeLocal(utcIso, timeZone),

    /**
     * Return `now + offsetHours` as `YYYY-MM-DDTHH:mm` in the user's timezone.
     * Use for default expiry timestamps in `<input type="datetime-local">`.
     */
    nowPlusHoursInUserTz: (hours: number): string =>
      _nowPlusHoursInUserTz(hours, timeZone),

    /** Current date as `YYYY-MM-DD` in the user's timezone. */
    todayInUserTz: (): string => _todayInUserTz(timeZone),

    /** `true` when a `YYYY-MM-DD` string is strictly before today in the user's timezone. */
    isBeforeToday: (dateStr: string): boolean => _isBeforeToday(dateStr, timeZone),

    /**
     * Parse a bare `YYYY-MM-DD` string into a DatePicker-compatible Date
     * (browser-local midnight).  Use instead of `new Date('YYYY-MM-DD')`.
     */
    parseDateOnlyToPickerDate: (dateStr: string | null | undefined): Date | null =>
      _parseDateOnlyToPickerDate(dateStr),

    /**
     * Convert a DatePicker-selected Date to a `YYYY-MM-DD` string.
     * Reads browser-local date components.  Use instead of
     * `d.toISOString().split('T')[0]` (which gives the UTC date).
     */
    pickerDateToDateOnly: (d: Date | null | undefined): string | undefined =>
      _pickerDateToDateOnly(d),
  };
}
